import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  CollectionUserAccessColleaguesService,
} from '../../../shared/shared-services/firebase/collection-user-access-colleagues.service';
import {
  SharedModalBasicComponent,
} from '../../../shared/shared-components/components/shared-modal-basic/shared-modal-basic.component';
import * as UserActions from '../user-store/user.actions';
import * as UserAccessActions from './user-access.actions';
import * as UserStoreActions from '../user-stores-store/user-store.actions';
import * as NotificationActions from '../../notifications/store/notification.actions';
import {catchError, ignoreElements, map, mergeMap, switchMap, take, withLatestFrom, delay, exhaustMap, filter} from 'rxjs/operators';
import {concat, EMPTY, from, of} from 'rxjs';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {select, Store} from '@ngrx/store';
import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import {IColleagueUser, IGroupAccess, IGroupAccessPerStore, IUserAccess, IUserAccessToken} from '../models';
import {selectColleagues} from '../../core/store-shared/shared.selectors';
import {CollectionGlobalService} from '../../../shared/shared-services/firebase/collection-global.service';
import {
  path_user_access_user_id_docs_user_token,
  path_user_access_userId,
} from '../../../shared/shared-services/database-paths';
import * as UserAccessSelectors from '../user-store/user.selectors';
import * as UserAccessFeatureSelectors from './user-access.selectors';
import {selectLoginProgressProperty} from '../user-store/user.selectors';
import {UserAccessService} from '../service/user-access-service/user-access.service';
import {calculatePercentile} from '../../../shared/shared-utils/number/number.utils';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {IModalDismissResult, ModalControllerService} from '../../../shared/shared-services/overlay/modal-controller.service';
import {Functions, httpsCallable} from '@angular/fire/functions';
import * as UserStoreSelectors from '../user-stores-store/user-store.selectors';
import {formatUserCreationErrorModalContent} from '../utils/user-access-error.utils';
import {ErrorName} from '../../../shared/shared-components/components/global-error-display/models/global-error.enum';
import {getUsableStoreIdsFromUserAccess} from '../utils/access.utils';
import {
  StaffAccessPasswordResetLinkModalComponent,
} from '../components/staff-access-password-reset-link-modal/staff-access-password-reset-link-modal.component';
import {
  IStaffAccessPasswordResetLink,
  StaffAccessPasswordResetLinkService,
} from '../service/staff-access-password-reset-link/staff-access-password-reset-link.service';

interface ICreateUserCallableResponse {
  messages: string[];
  success: boolean;
  email?: string;
  emailError?: string;
  emailSent?: boolean;
  passwordResetLink?: string;
}

@Injectable()
export class UserAccessEffects {

  //#region GET USER ACCESS
  getUserAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.getUserAccess),
      withLatestFrom(this.store.pipe(select(selectLoginProgressProperty))),
      switchMap(([action, loginSteps]) =>
        this.userAccessService.getDocumentAsObservable<IUserAccess>(
          path_user_access_userId(action.user.id),
        ).pipe(
          take(1),
          mergeMap((userAccess: IUserAccess) => {
            const storeIdsFromUserAccess: string[] = getUsableStoreIdsFromUserAccess(userAccess);

            return [
              UserAccessActions.getUserAccessSuccess({
                userAccess: {
                  ...userAccess,
                  userId: action.user.id,
                },
              }),
              UserActions.setLoginProgress({
                progress: {
                  ...loginSteps,
                  currentStep: storeIdsFromUserAccess.length > 0 ? 3 : 5,
                  percentComplete: calculatePercentile(
                    storeIdsFromUserAccess.length > 0 ? 3 : 5,
                    loginSteps.totalSteps,
                  ),
                },
              }),
              UserStoreActions.getStoresInformation({
                storeIds: storeIdsFromUserAccess,
                isLoggingIn: action.isLoggingIn,
              }),
            ];
          }),
          catchError((error: IGlobalError) => [
            NotificationActions.setGlobalErrorOccurrence({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
              },
            }),
            UserAccessActions.setPropertyOnUserAccessState({
              property: 'isUserAccessLoading',
              value: false,
            }),
            UserAccessActions.setPropertyOnUserAccessState({
              property: 'userAccess',
              value: undefined,
            }),
          ]),
        ),
      ),
    ),
  );
  //#endregion


  //[User Access] Show Modal
  showUserAccessModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.showUserAccessModal),
      delay(0),
      mergeMap((action) =>
        from(this.modalController.create({
          component: SharedModalBasicComponent,
          componentProps: action.modalProps,
          cssClass: ['shared-basic-modal-css-medium'],
        })).pipe(
          mergeMap((modalController) =>
            from(modalController.present()).pipe(
              mergeMap(() =>
                from(modalController.onDidDismiss()).pipe(
                  mergeMap((response: IModalDismissResult) => {
                    if (
                      action.modalProps.modalTitle === 'User Already Exists.'
                      && action.editedPermissions
                      && response?.data?.buttonPressed === 'button-accept'
                    ) {
                      return of(UserAccessActions.setColleaguesUserAccessForNonAccessibleUser({
                        userId: action.userId,
                        editedPermissions: action.editedPermissions,
                      }));
                    }
                    return EMPTY;
                  }),
                ),
              ),
            ),
          ),
          catchError((error) => {
            console.error(error);
            return EMPTY;
          }),
        ),
      ),
    ),
  );


  //[User Access] set Colleagues User Access
  setColleaguesUserAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.setColleaguesUserAccess),
      withLatestFrom(
        this.store.pipe(select(selectColleagues)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, colleagues, store, user]) =>
        from(
          this.collectionUserAccessColleaguesService.setColleagues(
            action.userId,
            action.editedPermissions,
            colleagues[action.userId],
            action.merge,
            action.newUser,
          ),
        ).pipe(
          map(() => UserAccessActions.setColleaguesUserAccessSuccess()),
          catchError((error) =>
            of(UserAccessActions.setColleaguesUserAccessFailure({
              userId: action.userId,
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        ),
      ),
    ),
  );

  //[User Access] set Colleagues User Access
  setColleaguesUserAccessForNonAccessibleUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.setColleaguesUserAccessForNonAccessibleUser),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionUserAccessColleaguesService.setNonAccessibleColleague(
            action.userId,
            action.editedPermissions,
          ),
        ).pipe(
          map(() => UserAccessActions.setColleaguesUserAccessForNonAccessibleUserSuccess()),
          catchError((error) => {
            return of(UserAccessActions.setColleaguesUserAccessForNonAccessibleUserFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            }));
          }),
        ),
      ),
    ),
  );

  //[User Access] Get Group Access Settings
  loadGroupAccessSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.getGroupAccessSettings),
      withLatestFrom(
        this.store.pipe(select(UserAccessFeatureSelectors.selectGroupAccessLoaded)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      filter(([, loaded]) => !loaded),
      exhaustMap(([action, , store, user]) =>
        from(this.firebaseService.getGroupAccessSettings()).pipe(
          map((result: IGroupAccess) =>
            UserAccessActions.getGroupAccessSettingsSuccess({groupAccess: result}),
          ),
          catchError((error) =>
            of(UserAccessActions.getGroupAccessSettingsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            })),
          ),
        ),
      ),
    ),
  );
  // [User Access][User Token] Get User Access Token
  getUserAccessToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.getUserAccessToken),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(
          this.collectionGlobalService.getDocument<{
            timestamp: number;
            token: string;
          }>(path_user_access_user_id_docs_user_token(user.id)),
        ).pipe(
          map((userToken: IUserAccessToken) => UserAccessActions.getUserAccessTokenSuccess({
            appFeature: action.appFeature,
            userAccessToken: userToken,
          })),
          catchError((error) => {
            return of(UserAccessActions.getUserAccessTokenFailure({
              appFeature: action.appFeature,
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store.storeId],
                userId: user.id,
              },
            }));
          }),
        ),
      ),
    ),
  );
  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  createStaffAccessColleague$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.createStaffAccessColleague),
      withLatestFrom(
        this.store.pipe(select(UserAccessSelectors.selectUser)),
        this.store.pipe(select(selectColleagues)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      ),
      mergeMap(([action, , colleagues, store]) => {
        const {userId, editedPermissions} = action;
        const createUserCallable = httpsCallable<
          { newUserEmail: string },
          ICreateUserCallableResponse
        >(this.functions, 'createUser');

        return from(
          createUserCallable({
            newUserEmail: userId,
          }),
        ).pipe(
          map((result) => result.data),
          mergeMap((res: ICreateUserCallableResponse) => {
            if (res.success && res.email && res.passwordResetLink && typeof res.emailSent === 'boolean') {
              return concat(
                of(UserAccessActions.setUserAccessOperationProgress({
                  progress: {
                    step: 2,
                    totalSteps: 2,
                    message: 'Applying store permissions…',
                    stepLabels: ['Create account', 'Apply store permissions'],
                  },
                })),
                from(
                  this.collectionUserAccessColleaguesService.setColleagues(
                    userId,
                    editedPermissions,
                    colleagues[userId],
                    false,
                    true,
                    false,
                  ),
                ).pipe(
                  map(() => {
                    this.staffAccessPasswordResetLinkService.setPasswordResetLink({
                      email: res.email,
                      emailError: res.emailError,
                      emailSent: res.emailSent,
                      passwordResetLink: res.passwordResetLink,
                    });
                    return UserAccessActions.createStaffAccessColleagueSuccess({userId});
                  }),
                  catchError((error) => {
                    const errorModalProps: ISharedModalBasic = {
                      buttonAccept: false,
                      buttonAcceptText: '',
                      buttonClose: true,
                      buttonCloseCross: false,
                      buttonCloseText: 'Continue',
                      modalTitle: 'Error: Account created but permissions not updated.',
                      modalTitleIcon: this.icons.bug,
                      modalTitleIconColor: this.iconColourEnum.red,
                      contextHeading: 'An unexpected error occurred.',
                      contextDescription: [
                        'We were unable to update the permissions for this user.',
                        'Please click on the user name and update the permissions manually.',
                      ],
                    };
                    return [
                      UserAccessActions.setPermissionsFailure({
                        error: {
                          ...error,
                          action: JSON.stringify(action),
                          actionType: action.type,
                          originalError: error,
                          storeIds: [store.storeId],
                          userId,
                        },
                        userId,
                      }),
                      UserAccessActions.showUserAccessModal({modalProps: errorModalProps}),
                    ];
                  }),
                ),
              );
            }

            if (res.success) {
              const missingPasswordResetLinkError: IGlobalError = {
                action: JSON.stringify(action),
                actionType: action.type,
                name: ErrorName.whyGlobalError,
                message: 'User created but no password reset link was returned.',
                originalError: res,
                storeIds: [store.storeId],
                userId,
              };
              const errorModalProps: ISharedModalBasic = {
                buttonAccept: false,
                buttonAcceptText: '',
                buttonClose: true,
                buttonCloseCross: false,
                buttonCloseText: 'Continue',
                modalTitle: 'Could Not Create User',
                modalTitleIcon: this.icons.bug,
                modalTitleIconColor: this.iconColourEnum.red,
                contextHeading: 'Password reset link unavailable',
                contextDescription: [
                  'The user account was created but no password reset link was returned.',
                  'Please try again or contact Gallix support.',
                ],
              };
              return [
                UserAccessActions.createStaffAccessColleagueFailure({error: missingPasswordResetLinkError, userId}),
                UserAccessActions.showUserAccessModal({modalProps: errorModalProps}),
              ];
            }

            if (res.messages?.[0] === 'Failed to create user: The email address is already in use by another account.') {
              const userExistsModalProps: ISharedModalBasic = {
                buttonAccept: false,
                buttonAcceptText: '',
                buttonClose: true,
                buttonCloseCross: false,
                buttonCloseText: 'Continue',
                modalTitle: 'User Already Exists.',
                modalTitleIcon: this.icons.person,
                modalTitleIconColor: this.iconColourEnum.yellow,
                contextHeading: 'This email is already associated with an existing account.',
                contextDescription: [
                  'Find the user in Staff Access and assign their store access from their user profile.',
                ],
              };
              const error: IGlobalError = {
                action: JSON.stringify(action),
                actionType: action.type,
                name: ErrorName.UsernameAlreadyInUseError,
                message: JSON.stringify(res.messages),
                originalError: res,
                storeIds: [store.storeId],
                userId,
              };
              return [
                UserAccessActions.createStaffAccessColleagueFailure({error, userId}),
                UserAccessActions.showUserAccessModal({modalProps: userExistsModalProps, userId}),
              ];
            }

            const userCreationError = formatUserCreationErrorModalContent(res.messages, userId);
            const errorModalProps: ISharedModalBasic = {
              buttonAccept: false,
              buttonAcceptText: '',
              buttonClose: true,
              buttonCloseCross: false,
              buttonCloseText: 'Continue',
              modalTitle: 'Could Not Create User',
              modalTitleIcon: this.icons.bug,
              modalTitleIconColor: this.iconColourEnum.red,
              contextHeading: userCreationError.heading,
              contextDescription: userCreationError.description,
            };
            const error: IGlobalError = {
              action: JSON.stringify(action),
              actionType: action.type,
              name: ErrorName.whyGlobalError,
              message: JSON.stringify(res.messages),
              originalError: res,
              storeIds: [store.storeId],
              userId,
            };
            return [
              UserAccessActions.createStaffAccessColleagueFailure({error, userId}),
              UserAccessActions.showUserAccessModal({modalProps: errorModalProps}),
            ];
          }),
          catchError((error) => [UserAccessActions.createStaffAccessColleagueFailure({error, userId})]),
        );

      }),
    ),
  );
  setNewServerAdminUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAccessActions.setNewServerAdminUser),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        const {userId, editedPermissions} = action;
        const createUserCallable = httpsCallable<
          { newUserEmail: string },
          ICreateUserCallableResponse
        >(this.functions, 'createUser');

        return from(
          createUserCallable({
            newUserEmail: userId,
          }),
        ).pipe(
          map((result) => result.data),
          mergeMap((res: ICreateUserCallableResponse) => {
            if (res.success && res.email && res.passwordResetLink && typeof res.emailSent === 'boolean') {
              return from(
                this.collectionUserAccessColleaguesService.setColleagues(
                  userId,
                  editedPermissions,
                  {} as IColleagueUser,
                  false,
                  true,
                  false,
                ),
              ).pipe(
                mergeMap(() => {
                  return concat(
                    of(UserAccessActions.setNewServerAdminUserSuccess({userId})),
                    from(this.presentPasswordResetLinkModal({
                      email: res.email,
                      emailError: res.emailError,
                      emailSent: res.emailSent,
                      passwordResetLink: res.passwordResetLink,
                    })).pipe(
                      ignoreElements(),
                      catchError(() => EMPTY),
                    ),
                  );
                }),
                catchError((error) => {
                  const errorModalProps: ISharedModalBasic = {
                    buttonAccept: false,
                    buttonAcceptText: '',
                    buttonClose: true,
                    buttonCloseCross: false,
                    buttonCloseText: 'Continue',
                    modalTitle: 'Error: Account created but permissions not updated.',
                    modalTitleIcon: this.icons.bug,
                    modalTitleIconColor: this.iconColourEnum.red,
                    contextHeading: 'An unexpected error occurred.',
                    contextDescription: [
                      'We were unable to update the permissions for this user.',
                      'Please click on the user name and update the permissions manually.',
                    ],
                  };
                  return [
                    UserAccessActions.setNewServerAdminUserFailure({
                      error: {
                        ...error,
                        action: JSON.stringify(action),
                        actionType: action.type,
                        originalError: error,
                        storeIds: [store.storeId],
                        userId: user.id,
                      },
                    }),
                    UserAccessActions.showUserAccessModal({modalProps: errorModalProps}),
                  ];
                }),
              );
            } else {
              const userExistsModalProps: ISharedModalBasic = {
                buttonAccept: true,
                buttonAcceptText: 'Add User To Stores',
                buttonClose: false,
                buttonCloseCross: false,
                buttonCloseText: 'Cancel',
                modalTitle: 'User Already Exists.',
                modalTitleIcon: this.icons.person,
                modalTitleIconColor: this.iconColourEnum.yellow,
                contextHeading: 'This email is already associated with an existing account.',
                contextDescription: ['Please Click Add User To Stores Button To Update The Users Permissions'],
              };
              const error: IGlobalError = {
                action: JSON.stringify(action),
                actionType: action.type,
                name: ErrorName.UsernameAlreadyInUseError,
                message: JSON.stringify(res.messages),
                originalError: res,
                storeIds: [store.storeId],
                userId: user.id,
              };
              return [
                UserAccessActions.setNewServerAdminUserFailure({error}),
                UserAccessActions.showUserAccessModal({modalProps: userExistsModalProps, editedPermissions, userId}),
              ];
            }
          }),
          catchError((error) => [UserAccessActions.setNewServerAdminUserFailure({error})]),
        );
      }),
    ),
  );


  constructor(
    private actions$: Actions,
    private functions: Functions,
    private collectionGlobalService: CollectionGlobalService,
    private collectionUserAccessColleaguesService: CollectionUserAccessColleaguesService,
    private modalController: ModalControllerService,
    private firebaseService: FirebaseService,
    private staffAccessPasswordResetLinkService: StaffAccessPasswordResetLinkService,
    private userAccessService: UserAccessService,
    private readonly store: Store,
  ) {
  }

  private async presentPasswordResetLinkModal(passwordResetLink: IStaffAccessPasswordResetLink): Promise<void> {
    const modal = await this.modalController.create({
      component: StaffAccessPasswordResetLinkModalComponent,
      componentProps: passwordResetLink,
      cssClass: ['staff-access-password-reset-link-modal'],
      backdropDismiss: false,
    });
    await modal.present();
    await modal.onDidDismiss();
  }
}
